import { storyblokEditable, StoryblokServerComponent } from '@storyblok/react/rsc';
import { resolveAsset, optimizeImage, getImageDimensions, colorStyle, srcSet } from '@/lib/helpers';
import CmsButton from '@/components/CmsButton';

const Hero = ({ blok }) => {
  const imageSrc = resolveAsset(blok.image);
  const imageDims = getImageDimensions(imageSrc);
  const cover = !!blok.image_cover;
  // Image column width: half by default; "two-fifths" gives the 60/40 split of
  // the old zz heroes, so a cover photo isn't zoomed in to fill a wide column.
  // Those heroes also put the image first on phones, with tighter text padding.
  const narrow = blok.image_width === 'two-fifths';
  // Lavender (#D3C1F8) is the default hero background; an editor pick overrides it.
  const style = colorStyle(blok, { into: { backgroundColor: '#D3C1F8' } });
  // Optional min-height (raw CSS value). When set, the two columns stretch to
  // fill it so the image is full-height and the content area centres vertically.
  const minH = blok.min_height || '';

  return (
    <section
      {...storyblokEditable(blok)}
      className="rounded-t-[30px] max-w-hero mx-4 mt-2 overflow-hidden"
      style={style}
    >
      <div
        className={`flex ${narrow ? 'flex-col-reverse' : 'flex-col'} sm:flex-row mx-auto ${cover || minH ? 'sm:items-stretch' : 'items-center'}`}
        style={minH ? { minHeight: minH } : undefined}
      >
        <div className={`w-full flex items-center justify-center md:p-14 ${narrow ? 'min-w-0 px-5 py-8 sm:p-10' : 'p-10'}`}>
          <div className="flex flex-col gap-6 md:gap-8 max-w-[700px] sm:min-w-[320px] md:min-w-[500px]">
          {blok.title && (
            <h1 className="text-32 md:text-48 lg:text-64 font-black leading-none">
              {blok.title}
            </h1>
          )}
          {blok.subtitle && (
            <p className="text-16 sm:text-20 md:text-24">{blok.subtitle}</p>
          )}
          <CmsButton label={blok.cta_text} link={blok.cta_link} style={blok.cta_style} showArrow={blok.cta_arrow} />
          {blok.body?.map((b) => <StoryblokServerComponent blok={b} key={b._uid} />)}
          </div>
        </div>
        {imageSrc && (narrow && !cover ? (
          // The old site's product-shot hero: a 40% column with the screenshot
          // fitted inside a 2rem margin, over the brand shape anchored bottom
          // right at a quarter strength.
          <div className="relative w-full sm:w-2/5 sm:shrink-0 self-stretch min-h-[calc(70vmin+4rem)] sm:min-h-0">
            {resolveAsset(blok.shape_image) && (
              <img
                src={resolveAsset(blok.shape_image)}
                alt=""
                aria-hidden="true"
                className="absolute right-0 bottom-0 h-full w-auto max-w-none mix-blend-multiply opacity-[0.24]"
              />
            )}
            <img
              src={optimizeImage(imageSrc, 1000, 0)}
              srcSet={srcSet(imageSrc)}
              sizes="(max-width: 640px) 100vw, 40vw"
              alt={blok.image?.alt || ''}
              className="absolute inset-8 w-[calc(100%-4rem)] h-[calc(100%-4rem)] object-contain"
              fetchPriority="high"
            />
          </div>
        ) : cover ? (
          <div className={`w-full relative self-stretch min-h-[280px] sm:min-h-0 ${narrow ? 'sm:w-2/5 sm:shrink-0' : ''}`}>
            <img
              src={optimizeImage(imageSrc, 1200, 0)}
              srcSet={srcSet(imageSrc)}
              sizes={narrow ? '(max-width: 640px) 100vw, 40vw' : '(max-width: 768px) 100vw, 50vw'}
              alt={blok.image?.alt || ''}
              className="absolute inset-0 w-full h-full object-cover"
              fetchPriority="high"
            />
          </div>
        ) : (
          <div className={`flex justify-center relative ${minH ? 'self-stretch' : ''}`}>
            {resolveAsset(blok.shape_image) && (
              <img
                src={resolveAsset(blok.shape_image)}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-contain opacity-[0.05]"
              />
            )}
            <img
              src={optimizeImage(imageSrc, 800, 0)}
              srcSet={srcSet(imageSrc)}
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 400px, 600px"
              alt={blok.image?.alt || ''}
              width={imageDims?.width || 800}
              height={imageDims?.height || 680}
              className={`relative object-contain ${minH ? 'h-full w-auto sm:max-w-[400px] md:max-w-[600px]' : 'max-h-[680px] sm:max-w-[400px] md:max-w-[600px] w-auto h-auto'}`}
              fetchPriority="high"
            />
          </div>
        ))}
      </div>
    </section>
  );
};

export default Hero;
